import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DxFormComponent, DxTreeListComponent } from 'devextreme-angular';
import { ToastrService } from 'ngx-toastr';
import {
  filter,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { LoadingService } from 'src/app/services/loading.service';
import { FileUtilService } from 'src/app/services/file-util.service';
import { ConfirmationDialogData } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import {
  LearningPathModel,
  LearningPathFullModel,
} from 'src/app/models/learning-paths/learning-path.model';
import { LearningPathService } from 'src/app/services/learning-path.service';
import { LearningPathCreationAndUpdateModel } from 'src/app/models/learning-path/learning-path-creation-and-update.model';
import { CourseTemplateService } from 'src/app/services/course-template.service';
import {
  LearningPathCourseAddModel,
  LearningPathCourseModel,
} from 'src/app/models/learning-path/learning-path-course.model';
import { CourseTemplateModel } from 'src/app/models/course/course-template.model';
import { LearningPathPersonsTableComponent } from 'src/app/components/learning-path-persons-table/learning-path-persons-table.component';
import { FONT_FAMILIES } from 'src/app/constants/fonts';
import { LINE_HEIGHTS } from 'src/app/constants/line-heights';
import { TextTemplateTypeModel } from 'src/app/models/text-templates/text-template-type.model';
import { UserContextService } from 'src/app/services/user-context.service';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';

@Component({
  selector: 'app-edit-learning-path',
  templateUrl: './edit-learning-path.component.html',
  styleUrl: './edit-learning-path.component.scss',
  standalone: false
})
export class EditLearningPathComponent implements OnInit {
  @ViewChild('personsTable') 
  personsTable!: LearningPathPersonsTableComponent;

  @ViewChild('form', { static: true }) form!: DxFormComponent;

  @ViewChild('coursesTreeList') set content(content: DxTreeListComponent) {
    if (content) {
      this.coursesTreeList = content;
    }
  }
  coursesTreeList!: DxTreeListComponent;

  fontFamilies = FONT_FAMILIES;
  lineHeights = LINE_HEIGHTS;

  formData = {
    name: '',
    description: '',
    descriptionExtended: '',
    image: '',
    parentId: '',
  };
  currentLearningPath: LearningPathFullModel | null = null;
  editingMode = false;
  selectedDocument = {
    name: '',
    dataBase64: '',
  };

  learningPathId: string | null = null;
  fileToRemoveId: string | null = null;
  showDeleteFileModal: boolean = false;
  modalConfirmationOptions: ConfirmationDialogData | null = null;

  showDeleteCourseModal = false;
  courseToRemoveId: string | null = null;
  deleteCourseModalOptions: ConfirmationDialogData | null = null;

  showWarningAddCoursePopup = false;
  addCourseModalOptions: ConfirmationDialogData | null = null;

  anyCompleted$!: Observable<boolean>;

  showAddCourseModal: boolean = false;
  allLearningPaths: LearningPathModel[] = [];
  allCourses: CourseTemplateModel[] = [];
  availableCourses: CourseTemplateModel[] = [];
  selectedCourseId: string | null = null;
  selectedCourseIdToAdd: string | null = null;
  selectedLearningPaths: string[] = [];

  private _learningPathCourses: LearningPathCourseModel[] = [];

  get learningPathCourses(): LearningPathCourseModel[] {
    return this._learningPathCourses;
  }

  set learningPathCourses(learningPathCourses: LearningPathCourseModel[]) {
    this._learningPathCourses = learningPathCourses;
    this.updateAvailableCourses(); // automatically update availableCourses
  }

  get isAddCourseDisabled(): boolean {
    const hasAnyNode = this.learningPathCourses.length > 0;
  
    if (hasAnyNode && !this.selectedCourseId) {
      return true;
    }
  
    if (this.selectedCourseId) {
      const hasChild = this.learningPathCourses.some(
        (course) => course.parentId === this.selectedCourseId
      );
      if (hasChild) return true;
    }
  
    return false;
  }

  updateAvailableCourses() {
    const addedCourseIds = new Set(
      this.learningPathCourses.map(c => c.eventId)
    );

    this.availableCourses = this.allCourses.filter(
      course => !addedCourseIds.has(course.id)
    );
  }


  descriptionTextTemplateType: TextTemplateTypeModel =
    TextTemplateTypeModel.ExtendedDescription;
  
  constructor(
    private activatedRoute: ActivatedRoute,
    private learningPathService: LearningPathService,
    private router: Router,
    private loadingService: LoadingService,
    private toastrService: ToastrService,
    private fileUtilService: FileUtilService,
    private translateService: TranslateService,
    private courseTemplateService: CourseTemplateService,
    public userContext: UserContextService,
    private pageStateService: PageStateService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(
        map((params) => params['id']),
        filter((id) => !!id),
        tap((id) => {
          this.editingMode = id !== 'new';

          if (this.editingMode) {
            this.learningPathId = id;
          }
        }),
        switchMap((id) =>
          this.editingMode
            ? this.loadingService.load(
                forkJoin({
                  learningPath: this.learningPathService.getLearningPathFull(id),
                  allLearningPaths:
                    this.learningPathService.getAllLearningPaths(),
                  allCourses:
                    this.courseTemplateService.getAvailableCourses(),
                  learningPathCourses:
                    this.learningPathService.getLearningPathCourses(id),
                })
              )
            : of({
                learningPath: null,
                allLearningPaths: [],
                allCourses: [],
                learningPathCourses: [],
              })
        )
      )
      .subscribe(
        ({
          learningPath,
          allLearningPaths,
          allCourses,
          learningPathCourses,
        }) => {
          this.currentLearningPath = learningPath;
          this.allCourses = allCourses;
          this.learningPathCourses = learningPathCourses;
          this.allLearningPaths = allLearningPaths;

          if (!learningPath) return;
          const imageFileUrl = learningPath.learningPath.imageFile?.fileUrl;

          if (imageFileUrl) {
            this.loadingService
              .load(this.fileUtilService.avatarUrlToBase64(imageFileUrl))
              .subscribe((base64) => {
                const mimeType = this.fileUtilService.getImageMimeType(imageFileUrl);
                this.formData.image = `data:${mimeType};base64,${base64}`;
              });
          }

          this.formData.name = learningPath.learningPath.name;
          this.formData.description =
            learningPath.learningPath.description || '';
          this.formData.descriptionExtended =
            learningPath.learningPath.descriptionExtended || '';

          if (this.learningPathId) {
            this.anyCompleted$ = this.loadingService
              .load(this.learningPathService.getAnyCompletedPersons(this.learningPathId!));
          }
        }
      );
  }

  navigateBack(): void {
    this.pageStateService.setFlag(
      PageStateFlags.RestoreLearningPathsOverview
    );
    
    this.router.navigate(['manage-learning-path']);
  }

  submit(): void {
    const formValid = this.form.instance.validate().isValid;
    if (!formValid) {
      return;
    }

    const model: LearningPathCreationAndUpdateModel = {
      name: this.formData.name,
      description: this.formData.description,
      descriptionExtended: this.formData.descriptionExtended,
      image: this.formData.image ? this.formData.image!.split(',')[1] : '',
    };

    if (this.editingMode && this.currentLearningPath) {
      const obs = this.learningPathService.updateLearningPath(
        this.currentLearningPath.learningPath.id,
        model
      );
      this.loadingService.load(obs).subscribe(() => {
        this.toastrService.success('Learning path saved');
      });
    } else {
      const obs = this.learningPathService.addLearningPath(model);
      this.loadingService.load(obs).subscribe((createdPath) => {
        this.toastrService.success('Learning path added');

        this.router.navigate([
          'manage-learning-path',
          createdPath.learningPath.id,
        ]);
      });
    }
  }

  onCategoryImageSelected(files: any) {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = () => {
      this.formData!.image = reader!.result!.toString();
    };
    reader.readAsDataURL(file);
  }

  onDocumentSelected(files: any) {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = () => {
      this.selectedDocument.name = file.name;
      this.selectedDocument.dataBase64 = reader!
        .result!.toString()
        .split(',')[1];

      this.loadingService
        .load(
          this.learningPathService.addLearningPathFile(
            this.currentLearningPath!.learningPath!.id,
            this.selectedDocument
          )
        )
        .subscribe((file) => {
          this.selectedDocument.name = '';
          this.selectedDocument.dataBase64 = '';
          this.currentLearningPath?.files.push(file);
        });
    };
    reader.readAsDataURL(file);
  }

  onRemoveFileClicked(file: any) {
    this.showDeleteFileModal = true;
    this.fileToRemoveId = file.fileId;

    this.modalConfirmationOptions = {
      hideUndo: true,
      submitButtonText: this.translateService.instant('COMMON.YES'),
      message: this.translateService.instant(
        'MANAGE-LEARNING-PATH.FORM.DELETE-LEARNING-PATH-FILE.TITLE'
      ),
      dismissButtonText: this.translateService.instant('COMMON.NO'),
    };
  }

  onRemoveFileConfirm() {
    this.loadingService
      .load(
        this.learningPathService.deleteLearningPathFile(
          this.currentLearningPath!.learningPath!.id,
          this.fileToRemoveId!
        )
      )
      .subscribe(() => {
        this.showDeleteFileModal = false;
        this.currentLearningPath!.files =
          this.currentLearningPath!.files.filter(
            (f) => f.fileId !== this.fileToRemoveId!
          );
      });
  }

  coursesSelectionChangedHandler(e: any) {
    e.component.option('selectionChangedFlag', true);

    if (e.selectedRowKeys.length) {
      this.selectedCourseId = e.selectedRowKeys[0];
    } else {
      this.selectedCourseId = null;
    }
  }

  coursesRowClickHandler(e: any) {
    if (e.component.option('selectionChangedFlag')) {
      e.component.option('selectionChangedFlag', false);
    } else {
      e.component.option('selectedRowKeys', []);
    }
  }

  onLearningPathSelected(e: any) {
    if (e.node.selected) {
      this.selectedLearningPaths = [...this.selectedLearningPaths, e.node.key];
    } else {
      this.selectedLearningPaths = this.selectedLearningPaths.filter(
        (id) => id !== e.node.key
      );
    }
  }

  onCourseSelected(e: any) {
    if (e.value) {
      this.selectedCourseIdToAdd = e.value;
    }
  }

  openAddCourseModal() {
    this.showAddCourseModal = true;
  }

  onCloseAddCourseModal() {
    this.showAddCourseModal = false;
    this.selectedCourseIdToAdd = null;
  }

  onAddModule() {
    this.anyCompleted$.subscribe((hasCompleted) => {
      if (hasCompleted) {
        this.showWarningAddCoursePopup = true;

        this.addCourseModalOptions = {
          hideUndo: true,
          submitButtonText: this.translateService.instant('COMMON.YES'),
          dismissButtonText: this.translateService.instant('COMMON.NO'),
          message: this.translateService.instant(
            'MANAGE-LEARNING-PATH.FORM.ADD-COURSE-TO-LEARNING-PATH-WARNING'
          ),
        };
      } else {
        this.addCourse();
      }
    });
  }

  onConfirmAddCourse(): void {
    this.showWarningAddCoursePopup = false;
    this.addCourse();
  }

  onShowModalChange(showModalStatus: boolean): void {
    if (!this.showWarningAddCoursePopup) {
      return;
    }

    this.showWarningAddCoursePopup = showModalStatus;

    if (!showModalStatus) {
      this.cancelAddCourse();
    }
  }
  
  cancelAddCourse(): void {
    this.showWarningAddCoursePopup = false;
    this.onCloseAddCourseModal();
    this.toastrService.info(
      this.translateService.instant(
        'COMMON.ACTION-CANCELLED'
      )
    );
  }

  addCourse() {
    const courseToAddModel: LearningPathCourseAddModel = {
      learningPathId: this.learningPathId!,
      eventId: this.selectedCourseIdToAdd!,
      parentId: this.selectedCourseId || null,
    };
    this.loadingService
      .load(this.learningPathService.addLearningPathCourse(courseToAddModel))
      .subscribe(() => {
        this.showAddCourseModal = false;
        this.selectedCourseIdToAdd = null;

        this.loadingService
          .load(
            this.learningPathService.getLearningPathCourses(
              this.learningPathId!
            )
          )
          .subscribe((learningPathCourses) => {
            this.learningPathCourses = [...learningPathCourses];
            this.personsTable.loadPersons();

            setTimeout(() => {
              const treeList = this.coursesTreeList?.instance;
              if (treeList) {
                treeList.expandRow(this.selectedCourseId);
              }
            });
          });
      });
  }

  onDeleteModuleClick(id: any) {
    this.showDeleteCourseModal = true;
    this.courseToRemoveId = id;

    this.deleteCourseModalOptions = {
      hideUndo: true,
      submitButtonText: this.translateService.instant('COMMON.YES'),
      dismissButtonText: this.translateService.instant('COMMON.NO'),
      title: this.translateService.instant(
        'MANAGE-LEARNING-PATH.FORM.DELETE-LEARNING-PATH-COURSE-DIALOG.TITLE'
      ),
      message: this.translateService.instant(
        'MANAGE-LEARNING-PATH.FORM.DELETE-LEARNING-PATH-COURSE-DIALOG.TEXT'
      ),
    };
  }

  onConfirmDeleteCourse() {
    if (!this.courseToRemoveId) return;

    this.loadingService
      .load(
        this.learningPathService.deleteLearningPathCourse(this.courseToRemoveId)
      )
      .subscribe(() => {
        this.selectedCourseIdToAdd = null;
      
        if(this.courseToRemoveId === this.selectedCourseId) {
          this.selectedCourseId = null;
        }

        this.courseToRemoveId = null;
        this.showDeleteCourseModal = false;
        
        this.loadingService
          .load(
            this.learningPathService.getLearningPathCourses(
              this.learningPathId!
            )
          )
          .subscribe((learningPathCourses) => {
            this.learningPathCourses = learningPathCourses;
            this.personsTable.loadPersons();
          });
      });
  }

  navigateInviteAttendees(): void {
    this.router.navigate([
      'learning-path',
      'invite',
      this.currentLearningPath!.learningPath.id
    ]);
  }

  navigateToActivityLog() {
    this.router.navigate(['activity-log'], {
      queryParams: { learningPathId: this.currentLearningPath!.learningPath.id },
    });
  }

  onTemplateContentChanged(content: string) {
    this.formData.descriptionExtended = content;
  }
}
